import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { stcService } from '../services/stcService'
import type { StcRecord, StcRecordStatus } from '../types/stc'
import './PptClaimsByPatientReport.css'

type GroupedClaims = {
  patientKey: string
  patientName: string
  claimNumbers: string[]
  total: number
  toPay: number
  paid: number
  failed: number
  lastClaimAt: string
}

export function PptClaimsByPatientReport() {
  const [statusFilter, setStatusFilter] = useState<'' | StcRecordStatus>('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [patientKeyFilter, setPatientKeyFilter] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['ppt-claims-by-patient', statusFilter, startDate, endDate, patientKeyFilter],
    queryFn: () => stcService.getRecords({
      ...(statusFilter && { status: statusFilter }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(patientKeyFilter.trim() && { patientKey: patientKeyFilter.trim() }),
      page: 1,
      pageSize: 500,
      sortBy: 'createdAt',
      sortDirection: 'desc',
    }),
  })

  const groups = useMemo<GroupedClaims[]>(() => {
    const items = data?.items || []
    const byPatient = new Map<string, GroupedClaims>()
    items.forEach((record: StcRecord) => {
      const key = (record.patientKey || 'UNKNOWN').trim()
      const existing = byPatient.get(key)
      const createdAt = record.createdAt || ''
      if (!existing) {
        byPatient.set(key, {
          patientKey: key,
          patientName: record.patientName || 'Unknown',
          claimNumbers: record.claimNumber ? [record.claimNumber] : [],
          total: 1,
          toPay: record.status === 'to_pay' ? 1 : 0,
          paid: record.status === 'paid' ? 1 : 0,
          failed: record.status === 'failed' ? 1 : 0,
          lastClaimAt: createdAt,
        })
        return
      }
      existing.total += 1
      if (record.claimNumber && !existing.claimNumbers.includes(record.claimNumber)) {
        existing.claimNumbers.push(record.claimNumber)
      }
      existing.toPay += record.status === 'to_pay' ? 1 : 0
      existing.paid += record.status === 'paid' ? 1 : 0
      existing.failed += record.status === 'failed' ? 1 : 0
      if (createdAt > existing.lastClaimAt) {
        existing.lastClaimAt = createdAt
      }
    })
    return Array.from(byPatient.values()).sort((a, b) => b.lastClaimAt.localeCompare(a.lastClaimAt))
  }, [data?.items])

  const exportCsv = () => {
    const rows = [
      ['Patient Key', 'Patient Name', 'Claim Numbers', 'Total Claims', 'To Pay', 'Paid', 'Failed', 'Last Claim At'].join(','),
      ...groups.map((group) => [
        `"${group.patientKey}"`,
        `"${group.patientName.replace(/"/g, '""')}"`,
        `"${group.claimNumbers.join(', ').replace(/"/g, '""')}"`,
        String(group.total),
        String(group.toPay),
        String(group.paid),
        String(group.failed),
        `"${group.lastClaimAt}"`,
      ].join(',')),
    ]
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ppt-claims-by-patient-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="ppt-claims-report-page">
      <div className="ppt-claims-report-header">
        <h1>PPT Claims by Patient Report</h1>
        <button className="btn-secondary" onClick={exportCsv} disabled={!groups.length}>
          Export CSV
        </button>
      </div>

      <div className="ppt-claims-report-filters">
        <label>
          Patient Key
          <input
            type="text"
            value={patientKeyFilter}
            onChange={(e) => setPatientKeyFilter(e.target.value)}
            placeholder="GMS#..., PPS#..., NAME#..."
          />
        </label>
        <label>
          Status
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as '' | StcRecordStatus)}>
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="signed">Signed</option>
            <option value="queued">Queued</option>
            <option value="to_pay">To Pay</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
          </select>
        </label>
        <label>
          From
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label>
          To
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
      </div>

      {isLoading && <div className="loading">Loading report...</div>}
      {error && <div className="error">Failed to load report data.</div>}

      {!isLoading && !error && (
        <div className="ppt-claims-report-table-wrap">
          <table className="ppt-claims-report-table">
            <thead>
              <tr>
                <th>Patient Key</th>
                <th>Patient</th>
                <th>Claim numbers</th>
                <th>Total</th>
                <th>To Pay</th>
                <th>Paid</th>
                <th>Failed</th>
                <th>Last Claim</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.patientKey}>
                  <td>{group.patientKey}</td>
                  <td>{group.patientName}</td>
                  <td>{group.claimNumbers.length ? group.claimNumbers.join(', ') : '-'}</td>
                  <td>{group.total}</td>
                  <td>{group.toPay}</td>
                  <td>{group.paid}</td>
                  <td>{group.failed}</td>
                  <td>{group.lastClaimAt ? new Date(group.lastClaimAt).toLocaleString() : '-'}</td>
                </tr>
              ))}
              {groups.length === 0 && (
                <tr>
                  <td colSpan={8} className="empty">
                    No claims found for the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
